

// src/context/CurrencyContext.js
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from 'auth/Auth';
import { fetchCompanyInfo, invalidateCompanyInfo } from 'utils/companyInfo';

// Define currency configuration with symbols, locales, and display formats
const CURRENCY_CONFIG = {
  USD: { 
    symbol: '$', 
    locale: 'en-US',
    label: 'USD (US Dollar)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  EUR: { 
    symbol: '€', 
    locale: 'de-DE', // Using German locale (comma for decimals)
    label: 'EUR (Euro)',
    decimalSeparator: ',',
    thousandSeparator: '.'
  },
  GBP: {
    symbol: '£',
    locale: 'en-GB',
    label: 'GBP (British Pound)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  AED: { 
    symbol: 'AED', 
    locale: 'ar-AE',
    label: 'AED (UAE Dirham)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  AUD: { 
    symbol: 'A$', 
    locale: 'en-AU',
    label: 'AUD (Australian Dollar)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  SGD: { 
    symbol: 'S$', 
    locale: 'en-SG',
    label: 'SGD (Singapore Dollar)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  CAD: { 
    symbol: 'C$', 
    locale: 'en-CA',
    label: 'CAD (Canadian Dollar)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  INR: { 
    symbol: '₹', 
    locale: 'en-IN',
    label: 'INR (Indian Rupee)',
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  CNY: { 
    symbol: 'CNY', 
    locale: 'zh-CN',
    label: 'CNY (Chinese Yuan)',
    decimalSeparator: '.',
    thousandSeparator: ','
  }
};

// Create available currencies array from the config
export const AVAILABLE_CURRENCIES = Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
  value: code,
  label: config.label
}));

// Create the context
const CurrencyContext = createContext();

// Create a provider component
export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('INR'); // Default currency
  const [isLoading, setIsLoading] = useState(true);
  const [availableCurrencies, setAvailableCurrencies] = useState(AVAILABLE_CURRENCIES);
  const { axiosInstance, isAuthenticated, isLoading: authLoading } = useAuth();
  const hasFetchedRef = useRef(false);

  // Load currencies from geography master when available
  useEffect(() => {
    if (!axiosInstance || !isAuthenticated) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { fetchCurrencies } = await import('api/countriesNow');
        const list = await fetchCurrencies(axiosInstance);
        if (cancelled || !Array.isArray(list) || list.length === 0) return;
        setAvailableCurrencies(
          list.map((c) => ({
            value: c.code,
            label: c.label || (c.name ? `${c.code} (${c.name})` : c.code),
          }))
        );
      } catch (err) {
        console.error('Failed to load currencies for CurrencyContext:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, isAuthenticated]);

  // Fetch company currency once after authentication is established
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      if (!authLoading && !isAuthenticated) {
        setIsLoading(false);
      }
      return;
    }

    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    const fetchCompanyCurrency = async () => {
      try {
        setIsLoading(true);
        // Shared with App.js's travel-flag lookup so the two do not each
        // request /company/info on load.
        const data = await fetchCompanyInfo();
        if (data && data.currency) {
          setCurrency(data.currency);
        }
      } catch (error) {
        console.error('Error fetching company currency:', error);
        hasFetchedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyCurrency();
  }, [authLoading, isAuthenticated, axiosInstance]);

  // Update currency in backend when it changes
  const updateCurrency = async (newCurrency) => {
    try {
      setCurrency(newCurrency);
      // Optionally update the backend
      await axiosInstance.put('/company/info', {
        currency: newCurrency,
      });
      invalidateCompanyInfo();
      return true;
    } catch (error) {
      console.error('Error updating currency:', error);
      return false;
    }
  };

  // Get currency label from the value
  const getCurrencyLabel = (value = currency) => {
    return CURRENCY_CONFIG[value]?.label || value;
  };

  // Get currency symbol from the code
  const getCurrencySymbol = (currencyCode = currency) => {
    return CURRENCY_CONFIG[currencyCode]?.symbol || currencyCode;
  };

  /**
 * Format a number according to the specified currency
 * @param {number|string} value - The monetary value to format
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @param {object} options - Additional formatting options
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value, currencyCode, options = {}) => {
  // Handle empty or invalid values
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // Parse the value to a number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle NaN values
  if (isNaN(numericValue)) {
    return '';
  }

  // Use explicit code when provided; otherwise company/default currency.
  // Never fall back to USD for an unknown code — that caused €/$ mismatches.
  const code = currencyCode || currency;
  const currConfig = CURRENCY_CONFIG[code] || {
    symbol: code || '',
    locale: 'en-US',
    decimalSeparator: '.',
    thousandSeparator: ',',
  };
  
  try {
    // Use Intl.NumberFormat for number formatting only (not currency style)
    const formatter = new Intl.NumberFormat(currConfig.locale, {
      style: 'decimal',  // Changed from 'currency' to 'decimal'
      minimumFractionDigits: options.minimumFractionDigits ?? 2,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
    });
    
    // Format the number and prepend our custom currency symbol
    const formattedNumber = formatter.format(numericValue);
    return `${currConfig.symbol} ${formattedNumber}`;
    
  } catch (error) {
    // Fallback to manual formatting if Intl.NumberFormat fails
    console.warn(`Error formatting currency ${code}:`, error);
    
    const { symbol, thousandSeparator, decimalSeparator } = currConfig;
    
    // Format the number manually
    const parts = numericValue.toFixed(2).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    const decimalPart = parts[1] || '00';
    
    return `${symbol} ${integerPart}${decimalSeparator}${decimalPart}`;
  }
};

  // Get deal-specific currency
  const getDealCurrency = async (dealId) => {
    try {
      // First fetch the deal details
      const { data: deal } = await axiosInstance.get(`/deals/${dealId}`);
      
      if (!deal) {
        throw new Error('Deal not found');
      }
      
      // If the deal already has a currency, use it directly
      if (deal.currency) {
        return {
          currencyCode: deal.currency,
          currencySymbol: getCurrencySymbol(deal.currency)
        };
      }
      
      // Check if deal has a lead_id
      if (deal.lead_id) {
        // Fetch lead details to get currency
        const { data: lead } = await axiosInstance.get(`/leads/${deal.lead_id}`);
        if (lead && lead.currency) {
          return {
            currencyCode: lead.currency,
            currencySymbol: getCurrencySymbol(lead.currency)
          };
        }
      }
      
      // If no lead or lead has no currency, use account currency
      if (deal.account_id) {
        const { data: account } = await axiosInstance.get(`/accounts/${deal.account_id}`);
        if (account && account.currency) {
          return {
            currencyCode: account.currency,
            currencySymbol: getCurrencySymbol(account.currency)
          };
        }
      }
      
      // Fall back to default currency if nothing found
      return {
        currencyCode: currency,
        currencySymbol: getCurrencySymbol(currency)
      };
      
    } catch (error) {
      console.error('Error fetching deal currency:', error);
      // Return default currency in case of error
      return {
        currencyCode: currency,
        currencySymbol: getCurrencySymbol(currency)
      };
    }
  };

  return (
    <CurrencyContext.Provider 
      value={{ 
        currency, 
        setCurrency, 
        updateCurrency, 
        availableCurrencies,
        getCurrencyLabel,
        getCurrencySymbol,
        getDealCurrency,
        formatCurrency,
        isLoading
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

// Create a custom hook to use the currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};